import React, { useEffect, useRef, useState } from 'react';

import { ValueBox, ValueStepper } from '@candu-ai/craft/ui';
import { getCssResetStyleMessage, StyleHierarchy, useCss } from 'craft/hooks';

interface Props {
  nodeIds: string[];
}

const maxZIndex = 2147483647;
const minZIndex = -500;

export const OverlayZindex = ({ nodeIds }: Props) => {
  const { css: overlayCss, setCss: setOverlayCss, stylesHierarchy } = useCss(nodeIds);

  const { fallbackValue, fallbackStyle } = stylesHierarchy?.zIndex ?? {
    fallbackValue: undefined,
    fallbackStyle: undefined,
  };

  const inputRef = useRef<HTMLInputElement>(null);

  const value = overlayCss?.zIndex ? parseFloat(overlayCss.zIndex) : undefined;
  const defaultValue = fallbackValue ? parseFloat(fallbackValue as string) : 5000;
  const displayValue = value || defaultValue;

  const [inputValue, setInputValue] = useState<number | undefined>(value);
  const [inEditMode, setInEditMode] = useState(false);

  const onSetValue = (newValue?: number) => {
    const valueChanged = newValue !== inputValue;
    setInEditMode(true);

    if (valueChanged) {
      setInputValue(newValue);

      // only trigger change when there is no value or the value is within the accepted range
      if (typeof newValue === 'undefined') {
        setOverlayCss({ zIndex: undefined });
      } else if (newValue >= minZIndex && newValue <= maxZIndex) {
        setOverlayCss({ zIndex: newValue });
      }
    }
  };

  useEffect(() => {
    // when value or type changes due to external factors (caller auto-adjusted value/type, viewport changed, node changed, etc.)
    // if we're not in edit mode, reflect value/type change in input
    if (!inEditMode) {
      if (displayValue !== inputValue) {
        setInputValue(displayValue);
      }
    }
  }, [displayValue, inEditMode]);

  return (
    <ValueBox
      label="Z-index"
      tooltip="Z-index controls which elements appear in front of others on a webpage."
    >
      <ValueStepper
        ref={inputRef}
        step={1}
        name="overlay-z-index"
        placeholder={!inEditMode ? 'unset' : undefined}
        editable
        format="inline"
        maxValue={maxZIndex}
        minValue={minZIndex}
        onChange={onSetValue}
        onBlur={() => setInEditMode(false)}
        value={inputValue}
        onResetTooltip={getCssResetStyleMessage(fallbackStyle || StyleHierarchy.Styleguide)}
        onReset={
          typeof inputValue !== 'undefined' && inputValue !== defaultValue
            ? () => {
                setOverlayCss({ zIndex: undefined });
                setInputValue(defaultValue);
              }
            : undefined
        }
      />
    </ValueBox>
  );
};
